import React from "react";

import { Button, Icon, Popover, PopoverContent, PopoverLauncher, ScreenClassProvider, Typography, useScreenClass } from "@vp/swan";
import { SurchargeApplicableProducts } from "../../constants/SurchargeApplicableProducts";
import { LocalizationsProvider, useLocalization } from "../../hooks/useLocalizations";
import { useShippingCountry } from "../../hooks/useShippingCountry";
import { getLocalizationsFor } from "../../localization/getLocalizations";
import { Locale } from "../../types/locales";
import { PageId } from "../../types/pageId";
import { SurchargeApplicableCountries, SurchargeApplicableLocales, SurchargeApplicablePageIds } from "../../types/surcharge";
import CSRWrapper from "../CSRWrapper";
import PriceErrorBoundary from "../PriceErrorBoundary";

type SurchargeMessageProps = {
  locale: Locale | string,
  productKey: string,
  version: number,
  pageId?: PageId,
};

const SurchargeMessageCore = (props: SurchargeMessageProps) => {
  const { locale, version, productKey, pageId } = props;
  const { t } = useLocalization();

  const isSurchargeApplicableForPage = pageId && SurchargeApplicablePageIds.includes(pageId);
  const isSurchargeApplicableForLocale = SurchargeApplicableLocales.some((unitLocale) => locale.toLowerCase() === unitLocale.toLowerCase());
  const isSurchargeApplicableForProduct = SurchargeApplicableProducts
    .some(({ productId, version: productVersion }) => productId === productKey && productVersion === version);

  const shippingCountry = useShippingCountry(locale);
  const isSurchargeApplicableForShippingCountry = SurchargeApplicableCountries.includes(shippingCountry);

  const screenClass = useScreenClass();
  const popoverPosition = screenClass === "xs" ? "top" : "right";

  const shouldRenderSurcharge =
    isSurchargeApplicableForPage &&
    isSurchargeApplicableForLocale &&
    isSurchargeApplicableForShippingCountry &&
    isSurchargeApplicableForProduct;

  return shouldRenderSurcharge ? (<>
    <Typography as="span" fontSkin="body-small">
      {t("price-platform.tariff-surcharge")}
    </Typography>
    <Popover position={popoverPosition} pl="2" as="span">
      <PopoverLauncher>
        <Button skin="unstyled">
          <Icon iconType="info" size="16p" aria-label={t("price-platform.tariff-surcharge-aria-label") as string} />
        </Button>
      </PopoverLauncher>
      <PopoverContent>
        <Typography fontSkin="body-small">{t("price-platform.tariff-surcharge-popover")}</Typography>
      </PopoverContent>
    </Popover>
  </>
  ) : (
    <></>
  );
};

const SurchargeMessage = (surchargeMessageProps: SurchargeMessageProps) => {
  return (
    <CSRWrapper>
      <LocalizationsProvider localizations={getLocalizationsFor(surchargeMessageProps.locale)}>
        <PriceErrorBoundary fallbackUi={<></>}>
          <ScreenClassProvider>
            <SurchargeMessageCore {...surchargeMessageProps} />
          </ScreenClassProvider>
        </PriceErrorBoundary>
      </LocalizationsProvider>
    </CSRWrapper>
  );
};

export { SurchargeMessage };
export type { SurchargeMessageProps };
